import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "../../store/useAppStore";
import { LogIn, Mail, Lock, EyeOff, Eye } from "lucide-react";
import { apiClient } from "../../lib/api";
import { getClaimsTokenPayload } from "../../lib/jwtUtils";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });

  const login = useAppStore((state) => state.login);
  const addNotification = useAppStore((state) => state.addNotification);
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = { email: "", password: "" };
    let isValid = true;

    // Validar email
    if (!email) {
      newErrors.email = "El correo electrónico es requerido";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Por favor ingresa un correo electrónico válido";
      isValid = false;
    }

    // Validar contraseña
    if (!password) {
      newErrors.password = "La contraseña es requerida";
      isValid = false;
    } else if (password.length < 6) {
      newErrors.password = "La contraseña debe tener al menos 6 caracteres";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Limpiar errores previos
    setErrors({ email: "", password: "" });

    // Validar formulario
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const auth = await apiClient.post("Login", {
        platform: "local",
        userName: email,
        password: password,
      }) as any;

      if (auth?.statusCode == "200") {
        const claims = getClaimsTokenPayload(auth?.data?.jwtToken);

        //console.log("PAYLOAD DEL TOKEN (CLAIMS):", claims);
        //const userRole = claims?.roles || "admin";

        // Verifica si el arreglo roles existe y contiene "Admin"
        const rawRoles = claims?.roles || [];
        const userRoles: string[] = Array.isArray(rawRoles) ? rawRoles : [rawRoles];

        const isAdmin = userRoles.some(rol => rol.toLowerCase() === "admin");

        // BLOQUEA SI NO ES ADMIN
        if (!isAdmin) {
          setIsLoading(false);
          addNotification({
            type: "error",
            message: "Acceso denegado. Se requiere rol de Administrador para entrar a este panel.",
          });
          return;
        }

        // Si pasa guarda al usuario
        const user = {
          id: claims?.identifier || "",
          name: claims?.userName || "",
          email: email,
          role: "admin" as const,
          //role: userRole as "admin" | "user" | "guest",
          token: auth?.data?.jwtToken,
          avatar: auth?.data?.photo
        };

        login(user);
        addNotification({
          type: "success",
          //message: `¡Bienvenido al panel de administración, ${user.name}!`,
          message: `¡Bienvenido al panel de administración!`,
        });
        navigate("/dashboard");
        setIsLoading(false);
      } else {
        setIsLoading(false);
        addNotification({
          type: "error",
          message: auth?.resultError?.message || "Error al iniciar sesión. Por favor, intenta de nuevo.",
        });
      }
    } catch (error: any) {
      setIsLoading(false);
      addNotification({
        type: "error",
        message: error?.resultError?.message || "Error al iniciar sesión. Por favor, intenta de nuevo.",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium text-foreground">
          Correo electrónico
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) setErrors({ ...errors, email: "" });
            }}
            placeholder="tu@email.com"
            className={`w-full rounded-soft border ${errors.email ? "border-destructive" : "border-input"
              } bg-background pl-10 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-smooth`}
          />
        </div>
        {errors.email && (
          <p className="text-xs text-destructive mt-1">{errors.email}</p>
        )}
      </div>

      <div className="space-y-2">
        <label
          htmlFor="password"
          className="text-sm font-medium text-foreground"
        >
          Contraseña
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            id="password"
            /* type="password" */
            type={showPassword ? "text" : "password"} // Alterna entre texto y puntos ocultos
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password) setErrors({ ...errors, password: "" });
            }}
            placeholder="••••••••"
            className={`w-full rounded-soft border ${errors.password ? "border-destructive" : "border-input"
              } bg-background pl-10 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-smooth`}
          />

          {/* Botón del ojo */}
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none transition-colors"
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
          </button>

        </div>
        {errors.password && (
          <p className="text-xs text-destructive mt-1">{errors.password}</p>
        )}
      </div>

      {/* <div className="flex items-center justify-between">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="rounded-sharp border-input text-primary focus:ring-2 focus:ring-ring"
          />
          <span className="text-sm text-muted-foreground">Recordarme</span>
        </label>
        <a href="#" className="text-sm text-primary hover:underline">
          ¿Olvidaste tu contraseña?
        </a>
      </div> */}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 rounded-soft bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
      >
        {isLoading ? (
          <>
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            Iniciando sesión...
          </>
        ) : (
          <>
            <LogIn className="h-4 w-4" />
            Iniciar sesión
          </>
        )}
      </button>

      {/* <div className="text-center text-sm text-muted-foreground">
        ¿No tienes una cuenta?{" "}
        <a href="#" className="text-primary hover:underline font-medium">
          Regístrate aquí
        </a>
      </div> */}
    </form>
  );
};

export default LoginPage;
